import { useEffect, useRef, useState, type DragEvent } from 'react';
import { Icon } from '@iconify/react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import { queryKeys } from '../../services/query/queryKeys';
import { teamPreferencesService } from '../../services/teamPreferences.service';
import type { TeamPreferences } from '../../services/teamPreferences.service';
import {
  COMPANY_LOGO_ACCEPT,
  formatCompanyLogoSize,
  renderCompanyLogoInWhite,
  validateCompanyLogoFile,
} from '../../utils/companyLogo';

interface CompanyLogoFieldProps {
  companyName?: string;
  logoUrl?: string;
  isDefaultLogo?: boolean;
  canEdit?: boolean;
}

interface PendingLogo {
  file: File;
  previewUrl: string;
}

const getInitials = (name?: string): string => {
  const words = (name || 'Company').trim().split(/\s+/).filter(Boolean);
  return (words.length > 1 ? `${words[0][0]}${words[1][0]}` : words[0]?.slice(0, 2) || 'CO').toUpperCase();
};

export default function CompanyLogoField({ companyName, logoUrl, isDefaultLogo = false, canEdit = false }: CompanyLogoFieldProps) {
  const { currentUser } = useAuth();
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [pendingLogo, setPendingLogo] = useState<PendingLogo | null>(null);
  const [isInspecting, setIsInspecting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const visibleLogoUrl = pendingLogo?.previewUrl || logoUrl;
  const renderLogoInWhite = renderCompanyLogoInWhite();

  useEffect(() => {
    setImageFailed(false);
  }, [visibleLogoUrl]);

  useEffect(() => {
    if (!canEdit) setPendingLogo(null);
  }, [canEdit]);

  useEffect(() => {
    const previewUrl = pendingLogo?.previewUrl;
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [pendingLogo?.previewUrl]);

  const inspectFile = async (file?: File) => {
    if (!file || !canEdit) return;

    setIsInspecting(true);
    setError(null);
    setSuccess(null);
    try {
      const validationError = await validateCompanyLogoFile(file);
      if (validationError) {
        setPendingLogo(null);
        setError(validationError);
        return;
      }

      setPendingLogo({ file, previewUrl: URL.createObjectURL(file) });
    } catch {
      setPendingLogo(null);
      setError('The browser could not inspect this image. Choose another logo and try again.');
    } finally {
      setIsInspecting(false);
    }
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    void inspectFile(event.dataTransfer.files?.[0]);
  };

  const handleUpload = async () => {
    if (!pendingLogo || !currentUser?.uid) return;

    setIsUploading(true);
    setError(null);
    setSuccess(null);
    try {
      const uploaded = await teamPreferencesService.updateCompanyLogo(pendingLogo.file);
      const teamPreferencesKey = queryKeys.preferences.team(currentUser.uid);
      queryClient.setQueryData<TeamPreferences>(teamPreferencesKey, (current) => ({
        ...(current || {}),
        logo_url: uploaded.url,
        company_logo_is_default: false,
        modules: current?.modules || {
          leads: false,
          outreach: false,
          sales: false,
          workers: false,
          nexus: false,
          chat: false,
          goals: false,
        },
      }));
      setPendingLogo(null);
      setSuccess('Company logo updated for everyone on this team.');
    } catch (uploadError: any) {
      setError(uploadError?.message || 'The company logo could not be uploaded.');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <section className="overflow-hidden rounded-[28px] border border-white/8 bg-[linear-gradient(145deg,rgba(82,96,255,0.10),rgba(255,255,255,0.025)_45%,rgba(255,255,255,0.015))]">
      <div className="grid gap-6 p-5 md:grid-cols-[180px_minmax(0,1fr)] md:p-6">
        <div className="relative flex min-h-44 items-center justify-center overflow-hidden rounded-[22px] border border-white/10 bg-[radial-gradient(circle_at_50%_35%,rgba(82,96,255,0.17),rgba(4,5,9,0.82)_65%)] p-5">
          <span className="absolute left-3 top-3 inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-black/35 px-2.5 py-1 text-[9px] font-black uppercase tracking-[0.14em] text-white/55">
            <span className={`h-1.5 w-1.5 rounded-full ${pendingLogo ? 'bg-amber-300' : isDefaultLogo ? 'bg-cyan-300' : logoUrl ? 'bg-emerald-300' : 'bg-white/30'}`} />
            {pendingLogo ? 'New preview' : isDefaultLogo ? 'Brainiacs default' : logoUrl ? 'Current logo' : 'No logo'}
          </span>

          {visibleLogoUrl && !imageFailed ? (
            <img
              src={visibleLogoUrl}
              alt={`${companyName || 'Company'} logo`}
              className={`mt-5 h-24 w-full object-contain drop-shadow-[0_12px_28px_rgba(0,0,0,0.38)] ${renderLogoInWhite ? 'brightness-0 invert' : ''}`}
              onError={() => setImageFailed(true)}
            />
          ) : (
            <span className="mt-5 flex h-24 w-24 items-center justify-center rounded-[24px] border border-white/12 bg-white/[0.055] text-2xl font-black tracking-[-0.04em] text-white/65 shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]">
              {getInitials(companyName)}
            </span>
          )}
        </div>

        <div className="min-w-0">
          <div className="flex items-start gap-3">
            <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-[#7580ff]/25 bg-[#5260FF]/15 text-indigo-100">
              <Icon icon="bx:image-alt" className="h-4.5 w-4.5" />
            </span>
            <div>
              <h3 className="text-lg font-black tracking-tight text-white">Company logo</h3>
              <p className="mt-1 text-sm leading-6 text-gray-400">
                {isDefaultLogo
                  ? 'Your team currently uses the Brainiacs logo. Upload your own brand mark whenever you are ready.'
                  : 'One shared brand mark for this team.'}
              </p>
            </div>
          </div>

          {!canEdit ? (
            <div className="mt-4 flex items-start gap-3 rounded-2xl border border-amber-300/18 bg-amber-300/[0.065] px-4 py-3.5 text-sm leading-5 text-amber-50/80">
              <Icon icon="bx:lock-alt" className="mt-0.5 h-4.5 w-4.5 shrink-0 text-amber-200" />
              <span><strong className="font-black text-amber-50">View only.</strong> Only Partners can update the company logo.</span>
            </div>
          ) : (
          <div
            className={`mt-4 rounded-2xl border border-dashed px-4 py-4 transition-colors ${
              isDragging
                ? 'border-[#7d87ff]/65 bg-[#5260FF]/12'
                : 'border-white/12 bg-black/20 hover:border-white/20'
            }`}
            onDragEnter={(event) => {
              event.preventDefault();
              setIsDragging(true);
            }}
            onDragOver={(event) => event.preventDefault()}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
          >
            <input
              ref={inputRef}
              type="file"
              accept={COMPANY_LOGO_ACCEPT}
              className="sr-only"
              aria-label="Choose company logo"
              onChange={(event) => {
                const file = event.currentTarget.files?.[0];
                event.currentTarget.value = '';
                void inspectFile(file);
              }}
            />

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                {pendingLogo ? (
                  <>
                    <p className="truncate text-sm font-bold text-white">{pendingLogo.file.name}</p>
                    <p className="mt-1 text-xs text-white/40">{formatCompanyLogoSize(pendingLogo.file.size)} · Ready to upload</p>
                  </>
                ) : (
                  <>
                    <p className="text-sm font-bold text-white/85">Drop your logo here</p>
                    <p className="mt-1 text-xs leading-5 text-white/40">JPG, PNG, or safe SVG · 30 MB maximum</p>
                  </>
                )}
              </div>

              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                disabled={isInspecting || isUploading}
                className="inline-flex h-9 shrink-0 items-center justify-center gap-2 rounded-xl border border-white/12 bg-white/[0.065] px-3.5 text-xs font-black text-white transition-colors hover:bg-white/10 disabled:cursor-wait disabled:opacity-50"
              >
                <Icon icon={isInspecting ? 'bx:loader-circle' : 'bx:folder-open'} className={`h-4 w-4 ${isInspecting ? 'animate-spin' : ''}`} />
                {isInspecting ? 'Checking…' : !isDefaultLogo && logoUrl ? 'Choose replacement' : 'Choose company logo'}
              </button>
            </div>
          </div>
          )}

          {error ? (
            <div role="alert" className="mt-3 flex items-start gap-2 rounded-xl border border-rose-400/25 bg-rose-400/10 px-3 py-2.5 text-xs font-semibold leading-5 text-rose-100">
              <Icon icon="bx:error-circle" className="mt-0.5 h-4 w-4 shrink-0" />{error}
            </div>
          ) : null}
          {success ? (
            <div role="status" className="mt-3 flex items-start gap-2 rounded-xl border border-emerald-400/25 bg-emerald-400/10 px-3 py-2.5 text-xs font-semibold leading-5 text-emerald-100">
              <Icon icon="bx:check-circle" className="mt-0.5 h-4 w-4 shrink-0" />{success}
            </div>
          ) : null}

          {pendingLogo ? (
            <div className="mt-4 flex flex-wrap justify-end gap-2">
              <button
                type="button"
                onClick={() => setPendingLogo(null)}
                disabled={isUploading}
                className="rounded-xl px-3.5 py-2 text-xs font-bold text-white/55 transition-colors hover:bg-white/[0.06] hover:text-white disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleUpload}
                disabled={isUploading}
                className="inline-flex items-center gap-2 rounded-xl border border-[#7580ff]/35 bg-[#5260FF] px-4 py-2 text-xs font-black text-white shadow-[0_12px_26px_rgba(82,96,255,0.25)] transition-colors hover:bg-[#4553f1] disabled:cursor-wait disabled:opacity-60"
              >
                <Icon icon={isUploading ? 'bx:loader-circle' : 'bx:cloud-upload'} className={`h-4 w-4 ${isUploading ? 'animate-spin' : ''}`} />
                {isUploading ? 'Uploading to Appwrite…' : !isDefaultLogo && logoUrl ? 'Update company logo' : 'Use company logo'}
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}
